import React, { useState, useEffect } from "react";
import useQueryParams from "../../../hooks/useQueryParams";
import { useUI } from "../../../context/ui";
import { useLocation, useNavigate } from "react-router-dom";
import { API_URL } from "../../../utilities/constants";
import PageTitle from "../../../components/common/PageTitle";
import FullPageLoader from "../../../components/ui/FullPageLoader";
import { formatDateTime } from "../../../utilities/helpers";
import Pagination from "../../../components/common/Pagination";
import { FaCheckCircle, FaHourglassHalf, FaTimesCircle } from "react-icons/fa";
import ButtonTextIcon from "../../../components/ui/ButtonTextIcon";
import { RiArrowGoBackFill } from "react-icons/ri";

const styles = `
  @media (max-width: 1080px) {
    .responsive-container1 {
      flex-direction: column !important;
    }
  }
`;

const buildQueryParams = (params) =>
  Object.entries(params)
    .filter(([_, value]) => value !== "")
    .map(([key, value]) => `${key}=${value}`)
    .join("&");

const STATUS_STYLES = {
  sending: { backgroundColor: "rgba(243, 243, 142, 0.56)", color: "#FF7A33" },
  sent: { backgroundColor: "#D1FAE5", color: "#047857" },
  failed: { backgroundColor: "#FEE2E2", color: "#DC2626" },
};

const StatusIndicator = ({ icon, count, type }) => {
  return (
    <div
      className="flex center-center b-r-8"
      style={{
        padding: "10px",
        height: "40px",
        width: "150px",
        ...STATUS_STYLES[type],
      }}
    >
      <span style={{ marginRight: "8px" }}>{icon}</span>
      <span className="f-w-400" style={{ color: "#374151" }}>
        {type.charAt(0).toUpperCase() + type.slice(1)}
      </span>
      <span className="mar-l-a" style={{fontSize: "18px" }}>{count}</span>
    </div>
  );
};

const SentNotificationDetails = () => {
  const [loading, setLoading] = useState(false);
  const { updateQueryParam, queryParams } = useQueryParams();
  const navigate = useNavigate();
  const location = useLocation();
  const groupid = queryParams.get("groupid") || "";
  const pageNo = queryParams.get("pageNo") || 0;
  const pageSize = queryParams.get("pageSize") || 10;

  const [notificationDetails, setNotificationDetails] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const { setSegmentTitle, addToastMessage } = useUI();

  useEffect(() => {
    setSegmentTitle("Notification Details");
  }, [location]);

  useEffect(() => {
    if (groupid) {
      fetchNotificationHistory();
    } else {
      navigate("/marketing?tab=app&section=history");
    }
  }, [groupid,location]);

  const fetchNotificationHistory = async () => {
    setLoading(true);
    try {
      const query = buildQueryParams({
        pageNo: pageNo,
        pageSize: pageSize,
      });
      const response = await fetch(
        API_URL + `/api/1.0.0/notification/${groupid}?sortOrder=ASC&${query}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "X-JWT-Assertion": localStorage.getItem("token"),
          },
        }
      );

      if (!response.ok) {
        setLoading(false);
        throw new Error("Failed to fetch templates");
      }

      const resp = await response.json();
      setNotificationDetails(resp.data);
      setSelectedUser(resp.data.list[0]); // Set the first user as selected by default
      setLoading(false);
    } catch (error) {
      console.error("Error fetching templates:", error);
    }
  };
  const handleUserClick = (user) => {
    setSelectedUser(user);
  };

  return (
    <>
      <style>{styles}</style>
      <PageTitle title="Customers/Notification" />
      <FullPageLoader isActive={loading} />

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          margin: "20px 20px",
          gap: "10px",
        }}
        className="responsive-container1"
      >
        <div
          style={{
            display: "flex",
            gap: "30px",
            color: "#777777",
          }}
        >
          <div>
            <ButtonTextIcon
              icon={<RiArrowGoBackFill />}
              style={{ with: "100px" }}
              onClick={() => navigate("/marketing?tab=app&section=history")}
            />
          </div>
          <div style={{ margin: "auto 0" }}>
            <strong>Sent By: {notificationDetails?.createdBy || "N/A"}</strong>
          </div>
          <div style={{ margin: "auto 0" }}>
            <strong>
              Sent At:{" "}
              {notificationDetails?.createdAt
                ? formatDateTime(notificationDetails.createdAt)
                : "N/A"}
            </strong>
          </div>
          <div style={{ margin: "auto 0" }}>
            <strong>
              Total:{" "}
              {notificationDetails?.successCount +
                notificationDetails?.inProgressCount +
                notificationDetails?.failedCount}{" "}
            </strong>
          </div>
        </div>
        <div
          style={{
            display: "flex",
            gap: "8px",
          }}
        >
          <StatusIndicator
            icon={<FaCheckCircle style={{ height: "12px" }} />}
            count={notificationDetails?.successCount || 0}
            type="sent"
          />
          <StatusIndicator
            icon={<FaHourglassHalf style={{ height: "12px" }} />}
            count={notificationDetails?.inProgressCount || 0}
            type="sending"
          />
          <StatusIndicator
            icon={<FaTimesCircle style={{ height: "12px" }} />}
            count={notificationDetails?.failedCount || 0}
            type="failed"
          />
        </div>
      </div>
      <div
        className="responsive-container1"
        style={{
          display: "flex",
          gap: "20px",
          padding: "0px 20px",
        }}
      >
        {notificationDetails?.list?.length > 0 ? (
          <>
            <div style={{ flex: 1, width: "100%" }}>
              <div
                style={{
                  backgroundColor: "#fff",
                  borderRadius: "8px",
                  boxShadow: "0px 0px 24px rgba(0, 0, 0, 0.04)",
                  border: "1px solid #f0f0f0",
                  padding: "20px",
                  width: "100%",
                }}
              >
                <p
                  style={{ marginBottom: "10px" }}
                  className="color-primary f-w-700"
                >
                  Users List
                  <p
                    style={{
                      color: "#777777",
                      fontSize: "12px",
                    }}
                    className="f-w-500"
                  >
                    Click on a user to view their notification details.
                  </p>
                </p>
                <div
                  style={{
                    height: "400px",
                    overflowY: "auto",
                    borderRadius: "8px",
                    scrollbarWidth: "thin",
                    marginBottom: "20px",
                    border: "1px solid rgba(135, 135, 135, 0.2)",
                  }}
                >
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                    }}
                    className="rs-table"
                  >
                    <thead>
                      <tr>
                        <th>#</th>
                        <th style={{ textAlign: "left", padding: "10px" }}>
                          Name
                        </th>
                        <th style={{ textAlign: "left", padding: "10px" }}>
                          Email
                        </th>
                        <th style={{ textAlign: "left", padding: "10px" }}>
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {notificationDetails.list?.map((user, i) => (
                        <tr
                          key={user.id}
                          style={{
                            cursor: "pointer",
                            backgroundColor:
                              selectedUser?.id === user.id
                                ? "rgba(235, 235, 235, 0.49)"
                                : "white",
                          }}
                          onClick={() => handleUserClick(user)}
                        >
                          <td>
                            {notificationDetails.pageNo *
                              notificationDetails.pageSize +
                              i +
                              1}
                          </td>
                          <td style={{ padding: "10px" }}>
                            {user?.name || "-"}
                          </td>
                          <td style={{ padding: "10px" }}>
                            {user?.email || "-"}
                          </td>
                          <td>
                            <p
                              style={{
                                padding: "10px",
                                width: "100px",
                                height: "25px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "12px",
                                borderRadius: "15px",
                                color:
                                  user.status === 1
                                    ? "#047857" // Success - Green
                                    : user.status === 2
                                    ? "#FFA500" // Pending - Orange
                                    : "#DC2626", // Failed - Red
                                backgroundColor:
                                  user.status === 1
                                    ? "#D1FAE5" // Light Green
                                    : user.status === 2
                                    ? "#FFF3E0" // Light Orange
                                    : "#FEE2E2", // Light Red
                              }}
                            >
                              {user?.status === 1
                                ? "Success"
                                : user?.status === 2
                                ? "Pending"
                                : "Failed"}
                            </p>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {notificationDetails && (
                  <Pagination
                    totalRecords={notificationDetails.totalRecords}
                    pageNo={notificationDetails.pageNo}
                    pageSize={notificationDetails.pageSize}
                    baseUrl={`?tab=app&section=template&groupid=${groupid}&`}
                  />
                )}
              </div>
            </div>
            <div
              style={{
                flex: 1,
                width: "100%",
              }}
            >
              <div
                style={{
                  backgroundColor: "#fff",
                  borderRadius: "8px",
                  boxShadow: "0px 0px 24px rgba(0, 0, 0, 0.04)",
                  border: "1px solid   #f0f0f0",
                  padding: " 20px ",
                  height: "100%",
                }}
              >
                <p style={{ marginBottom: "16px" }}>
                  <strong className="color-primary  f-w-700">User Name:</strong>{" "}
                  {selectedUser?.name || "-"}
                </p>
                {selectedUser && (
                  <div
                    style={{
                      overflowY: "auto",
                      borderRadius: "8px",
                      scrollbarWidth: "thin",
                    }}
                  >
                    <div style={{ marginBottom: "16px" }}>
                      <strong style={{ color: "#777777" }}>
                        Template Subject:
                      </strong>{" "}
                      {selectedUser?.templateSubject || "-"}
                    </div>
                    <div style={{ marginBottom: "16px" }}>
                      <strong style={{ color: "#777777" }}>
                        Template Body:
                      </strong>
                      <div
                        style={{
                          marginTop: "8px",
                          padding: "12px",
                          backgroundColor: "#ffffff",
                          border: "2px dashed #e0e0e0",
                          borderRadius: "8px",
                          overflowY: "auto",
                          height: "350px",
                        }}
                      >
                        <div style={{ marginBottom: "8px" }}>
                          {selectedUser?.templateBody || "-"}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        ) : (
          <div
            className=" flex center-center"
            style={{
              height: "400px",
              margin: "auto",
              width: "100%",
            }}
          >
            <p> No records found. </p>
          </div>
        )}
      </div>
    </>
  );
};

export default SentNotificationDetails;
